import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Category, maxPerBooking, Service } from '../../data/services';
import { colors, hairline, radii, spacing } from '../../theme';
import { useAreaAvailability } from '../../hooks/useAreaAvailability';
import Icon from '../ui/Icon';
import Text from '../ui/Text';
import ArtPanel from './ArtPanel';
import { cardDuration, cardPrice, cardWasPrice } from '../../data/hourly';

/** Whole rupees, grouped - `Intl` is not reliably present on Android JSC. */
const rupees = (value: number) =>
  '₹' + String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/** Width of the picture column. The copy takes whatever is left. */
/**
 * Dots in the rule under "View details".
 *
 * Enough to cross the label at a 5pt pitch (2pt dot + 3pt gap); the row is
 * clipped to the text's width, so spares at the end cost nothing and a short
 * label simply shows fewer.
 */
const DETAIL_DOTS = Array.from({ length: 24 }, (_, i) => i);

const ART_WIDTH = 96;
const ART_HEIGHT = 86;

/**
 * How far the add control hangs below the picture.
 *
 * Straddling the lower edge rather than sitting under it: the control belongs
 * to the picture above it, and a gap would let it read as the start of the
 * next row instead.
 */
const ADD_OVERLAP = 16;

type Props = {
  service: Service;
  category: Category;
  /** How many are in the cart. 0 shows "Add", anything else a stepper. */
  quantity: number;
  onChangeQuantity: (service: Service, delta: number) => void;
  onOpenDetails: (service: Service) => void;
  /** Draws the label above the name. Left off for ordinary rows. */
  popular?: boolean;
};

/**
 * One service, as a row in a listing.
 *
 * The counterpart to `ServiceCard`: the card is spare because it lives in a
 * scrolling rail where the tap is the point, and this is the opposite - the
 * end of the browse, where someone is comparing two services and wants the
 * price, the length and what is included without opening anything.
 *
 * Copy left, picture right. The picture is the smaller half here: by this
 * point the photograph has done its job of getting the tap, and what decides
 * the booking is written down.
 */
function ServiceRow({
  service,
  category,
  quantity,
  onChangeQuantity,
  onOpenDetails,
  popular = false,
}: Props) {
  // "Free" is `cardPrice`'s answer now, along with the /hr suffix an hourly
  // service needs - one function so a row and a card cannot label the same
  // price differently.
  const was = cardWasPrice(service, rupees);
  const inCart = quantity > 0;
  /* At the service's per-booking cap the + stops - `HomeScreen`'s `applyQuantity`
     would refuse the add anyway, and a live-looking button that does nothing
     reads as broken. */
  const cap = maxPerBooking(service);
  const atCap = quantity >= cap;

  /*
   * Not offered at the address on screen - see `useAreaAvailability`.
   *
   * Still pressable, all of it. A dead row cannot say why it is dead; this one
   * reaches the same two handlers every tap already goes through, and the
   * screen answers with the "coming soon" sheet.
   */
  const unavailable = useAreaAvailability().services.has(service.key);

  return (
    /*
     * The whole row opens the service.
     *
     * It used to be only the picture and the "View details" link, which left
     * the name, the price and the blurb - the parts people actually aim at -
     * inert. A list of cards where most of each card does nothing reads as
     * broken long before anyone finds the one word that works.
     *
     * The quantity controls stay separate targets nested inside: React Native
     * gives the innermost responder the touch, so Add and the +/- steppers
     * still do their own job and never fall through to opening the page.
     */
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`View details of ${service.name}`}
      onPress={() => onOpenDetails(service)}
      style={({ pressed }) => [styles.row, pressed ? styles.rowPressed : null]}
    >
      <View style={[styles.body, unavailable ? styles.dim : null]}>
        {popular ? (
          <Text variant="eyebrow" color={colors.primary}>
            POPULAR
          </Text>
        ) : null}

        <Text variant="subtitle" numberOfLines={2} style={styles.name}>
          {service.name}
        </Text>

        {/*
          No rating line here.

          It sat between the name and the price on every row of a long list,
          where "4.86 (330K reviews)" is the same claim under every service and
          pushes the two things actually being compared - what it costs and how
          long it takes - further apart.

          It said the rating "still shows on the detail page, where it is about
          one service and can be read". It does not any more, and for a
          different reason: the number was never real. See
          RATINGS_ARE_INVENTED in `ServiceScreen`.
        */}

        {/* Price and length on one line, the two things being weighed. */}
        <View style={styles.meta}>
          {/* Before the price and muted, so the eye lands on what is actually
              being charged. Absent on almost every service. */}
          {was ? (
            <Text variant="caption" color={colors.inkMuted} style={styles.was}>
              {was}
            </Text>
          ) : null}
          <Text variant="label" color={colors.primary}>
            {cardPrice(service, rupees)}
          </Text>
          <Text variant="caption" color={colors.inkMuted}>
            •
          </Text>
          <Text variant="caption" color={colors.inkMuted}>
            {cardDuration(service)}
          </Text>
        </View>

        {/* The blurb as a bullet, so it reads as one of a list of inclusions
            rather than as a sentence about the service. */}
        <View style={styles.bullet}>
          <Text variant="caption" color={colors.inkMuted}>
            •
          </Text>
          <Text variant="caption" color={colors.inkBody} style={styles.bulletText}>
            {service.blurb}
          </Text>
        </View>

        {/*
          Kept as a label, no longer as its own button.

          The row is the target now, so a nested Pressable here would be a
          second control doing exactly what the thing containing it does -
          which a screen reader announces twice, once per element, with the
          same words. As plain text it still does the job it was really
          there for: telling people the row leads somewhere.

          `ink`, not `inkBody`. On this row green belongs to the price and to
          Add; a third green made all three read as equally tappable.
          `inkBody` is not a softer dark though - it resolves to green800, the
          same value as `colors.primary`, so it put the label back to brand
          green. `ink` (green900) is the darkest step and the only one here
          that reads as ink rather than as a link.
        */}
        {/*
          The rule belongs to the words, not to the row.

          It used to be a full-width spacer under the whole column, which read
          as a divider closing the row off rather than as a mark on the label
          - and on a row whose picture sits to the right it ran out under
          nothing. Wrapping the text makes it exactly as wide as "View
          details", which is what an underline on a link looks like.

          `alignSelf: flex-start` is what keeps it that width: a plain View in
          a column stretches to fill, and the rule would span the column
          again.
        */}
        <View style={styles.details}>
          <Text variant="labelSm" color={colors.ink}>
            View details
          </Text>
          {/*
            Drawn dots, not a dotted border.

            `borderStyle: 'dotted'` at 1pt is unreliable in React Native - on
            Android it commonly rasterises to a solid hairline, and the
            transparent-sides workaround it needs is fragile besides. A row of
            small square Views is what the step rail on the detail page uses
            and it renders identically everywhere.

            The row is ABSOLUTELY POSITIONED, which is the whole trick.

            In flow it was a sibling of the label, so the block sized itself to
            whichever was wider - and 24 dots at 2pt on a 3pt pitch is 117pt
            against about 70pt of text, so the rule set the width and hung 45pt
            past the words. `overflow: 'hidden'` did not help: it clips what
            exceeds the container's own box, and the container was being sized
            by the very children it was meant to clip.

            Out of flow the row cannot contribute width. The label alone sizes
            the block, and `left: 0, right: 0` stretches the rule to exactly
            that - so the dots stop under the last letter at any text size, in
            any language, without the count needing to match the word.
          */}
          <View style={styles.detailsRule}>
            {DETAIL_DOTS.map(dot => (
              <View key={dot} style={styles.detailsDot} />
            ))}
          </View>
        </View>
      </View>

      <View style={styles.art}>
        {/* No Pressable of its own any more - the row around it is one. */}
        <ArtPanel
          image={service.image}
          icon={category.icon}
          style={unavailable ? [styles.panel, styles.dim] : styles.panel}
        />

        {/*
          Every control below sits INSIDE the row's own `Pressable`, so each
          one has to stop its press bubbling. Without that, tapping "+" both
          changed the quantity and opened the service's details sheet over
          the top - and the sheet has its own add, so one tap could put two
          of a service in the cart.
        */}
        {inCart ? (
          <View style={styles.stepper}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={`Remove one ${service.name}`}
              onPress={event => {
                event.stopPropagation();
                onChangeQuantity(service, -1);
              }}
              hitSlop={8}
              style={styles.step}
            >
              <Icon name="minus" size={14} color={colors.primary} strokeWidth={2} />
            </Pressable>

            <Text variant="labelSm" color={colors.primary}>
              {quantity}
            </Text>

            <Pressable
              accessibilityRole="button"
              accessibilityLabel={
                atCap
                  ? `Maximum ${cap} ${service.name} per booking`
                  : `Add another ${service.name}`
              }
              accessibilityState={{ disabled: atCap }}
              /* Still takes the press when disabled, so it does not fall
                 through to the row and open the details sheet. */
              onPress={event => {
                event.stopPropagation();
                if (!atCap) {
                  onChangeQuantity(service, 1);
                }
              }}
              hitSlop={8}
              style={styles.step}
            >
              <Icon
                name="plus"
                size={14}
                color={atCap ? colors.inkDisabled : colors.primary}
                strokeWidth={2}
              />
            </Pressable>
          </View>
        ) : (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={
              unavailable
                ? `${service.name} is coming soon to this area`
                : `Add ${service.name} to cart`
            }
            onPress={event => {
              event.stopPropagation();
              onChangeQuantity(service, 1);
            }}
            style={({ pressed }) => [
              styles.add,
              unavailable ? styles.addUnavailable : null,
              pressed ? styles.pressed : null,
            ]}
          >
            {/* "Soon", not "Coming soon": this button is sized for "Add", and
                two words would wrap or clip on a narrow phone. The sheet it
                opens says the rest. */}
            <Text
              variant="labelSm"
              color={unavailable ? colors.inkMuted : colors.primary}
            >
              {unavailable ? 'Soon' : 'Add'}
            </Text>
          </Pressable>
        )}
        {inCart && atCap ? (
          <Text
            variant="caption"
            color={colors.inkMuted}
            align="center"
            style={styles.capHint}
          >
            {`Max ${cap} per booking`}
          </Text>
        ) : null}
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  /*
   * Faded, not hidden and not desaturated. `filter` is not drawn on every
   * Android this app runs on, and opacity is - so this looks the same on a
   * budget phone as on a flagship.
   */
  dim: {
    opacity: 0.45,
  },
  addUnavailable: {
    backgroundColor: colors.surfaceMuted,
    borderColor: colors.border,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    paddingVertical: spacing.lg,
  },
  body: {
    flex: 1,
    /* Zero basis and a zero minimum, so the text column yields instead of
       pushing the picture off the right edge. A flex child's default minimum
       is its CONTENT width, which for a long service name is wider than the
       space left over - so the row grew past the screen and both ends were
       clipped. */
    flexBasis: 0,
    minWidth: 0,
    gap: spacing.xs,
  },
  /*
   * `fontWeight` as well as the variant's family, for the same reason the
   * section headings carry one: assets/fonts does not exist, so the named
   * face resolves to nothing and falls back to the system regular. Size is
   * left alone - only the weight was asked to match the heading.
   */
  name: {
    fontWeight: '700',
  },
  meta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  /* The strike itself. Nothing else in this app crosses text out. */
  was: {
    textDecorationLine: 'line-through',
  },
  /* Ruled off from the price line above it: the bullets are what is included,
     which is a different kind of fact from what it costs and how long it
     takes. Only drawn when there is a bullet to rule off. */
  bullet: {
    flexDirection: 'row',
    gap: spacing.xs,
    marginTop: spacing.xs,
    paddingTop: spacing.sm,
    paddingRight: spacing.sm,
    borderTopWidth: hairline.borderWidth,
    borderTopColor: hairline.borderColor,
  },
  /* Without this the bullet's text cannot wrap: a `Text` in a row takes its
     intrinsic width and pushes past the edge rather than breaking. */
  bulletText: {
    flex: 1,
  },
  /*
   * Indented to sit under the bullet's TEXT, not under its dot.
   *
   * The blurb above is a two-column row - a "•" then the words - so its text
   * starts one glyph and one gap in from the column edge. "View details" was
   * drawn straight against that edge, which left it hanging to the left of
   * everything it belongs under and reading as a stray line rather than as
   * the end of the block.
   *
   * The dot renders about 4pt wide at `caption`, plus the row's `spacing.xs`
   * gap. Matching that here lines the two up without wrapping this label in a
   * bullet row of its own.
   */
  details: {
    alignSelf: 'flex-start',
    /* Room beneath the label for the rule, which is out of flow and so adds
       no height of its own. */
    paddingBottom: 4,
    /* Indented to sit under the bullet's words rather than against the
       column's edge, where it hung left of everything it belongs under. */
    marginLeft: spacing.md,
    marginTop: spacing.xs,
  },
  detailsRule: {
    /*
     * Out of flow, pinned to both edges of the label above it.
     *
     * This is what holds the rule to the width of the words: a positioned box
     * does not size its parent, so the label decides the block's width and
     * `left`/`right` make the rule match it. `overflow` then has a real box to
     * clip against, and trims the surplus dots under the last letter.
     */
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    flexDirection: 'row',
    gap: 3,
    overflow: 'hidden',
  },
  detailsDot: {
    width: 2,
    height: 1,
    backgroundColor: colors.borderStrong,
  },

  /* Extra room at the foot for the control that straddles the picture. */
  art: {
    width: ART_WIDTH,
    /* Never squeezed below its own width - the picture and the Add button
       under it are a fixed pair, and the text column is what gives way. */
    flexShrink: 0,
    alignItems: 'center',
    paddingBottom: ADD_OVERLAP,
  },
  panel: {
    width: ART_WIDTH,
    height: ART_HEIGHT,
  },
  /* Pulled up over the picture's lower edge rather than positioned absolutely,
     so the row's height still accounts for it. */
  add: {
    marginTop: -ADD_OVERLAP,
    minWidth: 76,
    paddingVertical: spacing.sm,
    alignItems: 'center',
    borderRadius: radii.sm,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.primary,
  },
  stepper: {
    marginTop: -ADD_OVERLAP,
    minWidth: 76,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    borderRadius: radii.sm,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.primary,
  },
  step: {
    width: 20,
    alignItems: 'center',
  },
  capHint: {
    marginTop: spacing.xs,
  },
  pressed: {
    opacity: 0.6,
  },
  /*
   * The whole-row press, which is a different gesture from a button's.
   *
   * A button dims to 0.6 because it is small and the finger is on top of it.
   * Fading an entire row that way takes the picture and the price down with
   * it and reads as the row being disabled. A faint wash behind it says
   * "this is the one you are touching" without changing anything on it.
   */
  rowPressed: {
    backgroundColor: colors.surfaceAccent,
  },
});

export default React.memo(ServiceRow);
